import React from 'react';
import usePageTitle from '../../hooks/usePageTitle';
import { Link, useLocation } from 'react-router-dom';
import SiteHeader from './SiteHeader';
import SiteFooter from './SiteFooter';
import { PAGE_TRAILS, ROUTES } from './siteNav';

const CTA_NAVY = '#213D61';

/** Purchase prompt that rides the bottom of the viewport as you read. */
const ScamAwareCta = () => (
  <div className="sticky bottom-0 z-30 w-full" style={{ backgroundColor: CTA_NAVY }}>
    <div className="mx-auto flex w-full max-w-[1500px] items-center gap-4 px-4 py-4 md:px-10">
      <img
        src="/images/site/nav/ai-cloud-white.png"
        alt=""
        aria-hidden="true"
        className="h-10 w-auto flex-shrink-0 md:h-12"
      />
      <p className="flex-1 text-lg text-white md:text-2xl">Supercharge your scam awareness &raquo;</p>
      <Link
        to={ROUTES.getStarted}
        className="flex-shrink-0 rounded-[6px] bg-white px-6 py-2 text-base text-black transition-colors hover:bg-white/90 md:px-12 md:py-3 md:text-xl"
      >
        Buy now
      </Link>
    </div>
  </div>
);

/**
 * Shell for every content sub-page: the same nav bar as the homepage, the page
 * body, the purchase prompt, and the site footer.
 *
 * `title` no longer renders on the page — it names the browser tab.
 */
/**
 * Breadcrumb strip under the nav bar. Sits in the header chrome rather than in
 * the page body so it reads as part of the navigation, and is looked up from
 * the path so no page has to pass its own trail.
 */
const NavTrail = () => {
  const { pathname } = useLocation();
  const trail = PAGE_TRAILS[pathname];
  if (!trail) return null;

  return (
    // Same grey as the nav bar above it, with no rule between: the mockup
    // treats the breadcrumb as the bar's second line, not a separate strip.
    <div className="w-full" style={{ backgroundColor: '#D9D9D9' }}>
      {/* Mirrors the header row's own layout -- same max width, same padding,
          same gap, plus a spacer standing in for the wordmark -- so the trail
          starts where the nav items start rather than at the page edge. */}
      <div className="mx-auto flex w-full max-w-[1500px] gap-4 px-4 pb-3 md:px-8">
        <span aria-hidden="true" className="hidden h-0 w-[108px] flex-shrink-0 lg:block" />
        <nav
          aria-label="Breadcrumb"
          className="text-[13px] text-black md:text-[15px]"
        >
          <Link to="/" className="hover:underline">
            Home
          </Link>
          {trail.map((crumb) => (
            <span key={crumb}>
              <span aria-hidden="true" className="px-2">
                &raquo;
              </span>
              {crumb}
            </span>
          ))}
        </nav>
      </div>
    </div>
  );
};

const SubPage = ({ title, children }) => {
  usePageTitle(title);

  return (
    <div className="flex min-h-screen flex-col bg-white">
      {/* Same header as the homepage -- no section variant. The nav bar is
          identical on every page; only the breadcrumb below it changes. */}
      <div className="sticky top-0 z-50">
        <SiteHeader />
      </div>
      <NavTrail />
      <main className="flex flex-1 flex-col">
        {/* Clearance above the sticky purchase bar. Set here rather than per
            page: without it the last section butts straight into the bar. */}
        <div className="flex-1 pb-16 md:pb-24">{children}</div>
        <ScamAwareCta />
      </main>
      <SiteFooter />
    </div>
  );
};

export default SubPage;
